/**
 * Embed Socket Hook
 * Provides Socket.IO connection for real-time AI responses in the embed widget
 */

import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';

import { useEmbedConfig } from '../providers/EmbedConfigProvider';

let sharedSocket: Socket | null = null;
let sharedSocketUrl: string | null = null;

export interface UseEmbedSocketResult {
  socket: Socket | null;
  isConnected: boolean;
  socketId: string | null;
}

/**
 * Custom hook for Socket.IO connection in embed widget
 * Connects to the asset API WebSocket for real-time AI responses
 */
export function useEmbedSocket(token?: string | null): UseEmbedSocketResult {
  const { assetApiUrl, appBaseUrl } = useEmbedConfig();
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [socketId, setSocketId] = useState<string | null>(null);

  // Determine the socket URL - use assetApiUrl if available, otherwise derive from appBaseUrl
  const socketUrl =
    assetApiUrl ||
    (appBaseUrl ? appBaseUrl.replace('://localhost', '://asset-api.qa.bldr.it').replace(/:\d+/, '') : '');

  useEffect(() => {
    if (!socketUrl) return;

    // For localhost development, use the QA asset API for websocket so we can
    // test the full flow without a local socket server.
    const effectiveSocketUrl = socketUrl.includes('localhost') ? 'https://asset-api.qa.bldr.it' : socketUrl;

    if (!socketRef.current || sharedSocketUrl !== effectiveSocketUrl) {
      if (sharedSocket && sharedSocketUrl !== effectiveSocketUrl) {
        sharedSocket.disconnect();
        sharedSocket = null;
      }

      socketRef.current =
        sharedSocket ||
        io(effectiveSocketUrl, {
          autoConnect: false,
          withCredentials: true,
          transports: ['websocket', 'polling'],
          forceNew: false,
          timeout: 20000,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
          extraHeaders: {
            'Access-Control-Allow-Origin': '*',
          },
        });
      sharedSocket = socketRef.current;
      sharedSocketUrl = effectiveSocketUrl;
    }

    const socket = socketRef.current;

    if (token) {
      socket.auth = { token };
    }

    if (!socket.connected) {
      socket.connect();
    } else {
      setIsConnected(true);
      setSocketId(socket.id || null);
    }

    const handleConnect = () => {
      setIsConnected(true);
      setSocketId(socket.id || null);
    };

    const handleDisconnect = () => {
      setIsConnected(false);
      setSocketId(null);
    };

    const handleConnectError = (error: any) => {
      console.error('[EmbedSocket] Connection error:', error?.message || error);
      setIsConnected(false);
      setSocketId(null);
    };

    const handleReconnect = () => {
      if (token) {
        socket.auth = { token };
      }
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);
    socket.on('reconnect', handleReconnect);

    if (socket.connected) {
      setIsConnected(true);
      setSocketId(socket.id || null);
    }

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleConnectError);
      socket.off('reconnect', handleReconnect);
    };
  }, [socketUrl, token]);

  return {
    socket: socketRef.current,
    isConnected,
    socketId,
  };
}

export default useEmbedSocket;
